import type { UserRole } from '../types/auth';

/**
 * Permission Service
 *
 * Provides role-based permission checking for frontend routing and UI rendering.
 * Implements role hierarchy: SUPERADMIN > ADMIN > HOST > PARTICIPANT
 *
 * Note: These are frontend checks for UX purposes. Backend always enforces
 * actual permissions on API requests. Frontend checks should never be the
 * sole security mechanism.
 */

// ============================================================================
// ROLE HIERARCHY
// ============================================================================

/**
 * Role hierarchy for permission checking
 * Higher level roles have all permissions of lower level roles
 */
const ROLE_HIERARCHY: Record<UserRole, number> = {
  PARTICIPANT: 1,
  HOST: 2,
  ADMIN: 3,
  SUPERADMIN: 4,
};

/**
 * Check if a role has a minimum required role level
 */
export const hasMinimumRole = (
  userRole: UserRole | null | undefined,
  requiredRole: UserRole,
): boolean => {
  if (!userRole) return false;
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
};

/**
 * Check if a role has a specific role (exact match)
 */
export const hasRole = (
  userRole: UserRole | null | undefined,
  role: UserRole,
): boolean => {
  if (!userRole) return false;
  return userRole === role;
};

/**
 * Check if a user role is in a list of allowed roles
 */
export const hasAnyRole = (
  userRole: UserRole | null | undefined,
  allowedRoles: UserRole[],
): boolean => {
  if (!userRole) return false;
  return allowedRoles.includes(userRole);
};

// ============================================================================
// ROLE-BASED ACCESS PATTERNS
// ============================================================================

/**
 * Check if user can access host features
 * HOST+ users can access host functionality
 */
export const canAccessHostFeatures = (userRole: UserRole | null | undefined): boolean => {
  return hasMinimumRole(userRole, 'HOST');
};

/**
 * Check if user can access admin features
 * ADMIN+ users can access admin functionality
 */
export const canAccessAdminFeatures = (userRole: UserRole | null | undefined): boolean => {
  return hasMinimumRole(userRole, 'ADMIN');
};

/**
 * Check if user can access superadmin features
 * SUPERADMIN only
 */
export const canAccessSuperAdminFeatures = (
  userRole: UserRole | null | undefined,
): boolean => {
  return hasMinimumRole(userRole, 'SUPERADMIN');
};

/**
 * Check if user is participant level
 * Any authenticated user is at least PARTICIPANT
 */
export const isParticipant = (userRole: UserRole | null | undefined): boolean => {
  return userRole !== null && userRole !== undefined;
};

// ============================================================================
// FEATURE-SPECIFIC PERMISSIONS
// ============================================================================

/**
 * Check if user can create sessions
 * HOST+ only
 */
export const canCreateSessions = (userRole: UserRole | null | undefined): boolean => {
  return canAccessHostFeatures(userRole);
};

/**
 * Check if user can manage participants
 * HOST+ only
 */
export const canManageParticipants = (
  userRole: UserRole | null | undefined,
): boolean => {
  return canAccessHostFeatures(userRole);
};

/**
 * Check if user can view analytics for all sessions
 * ADMIN+ only (HOST can only see their own sessions)
 */
export const canViewAllAnalytics = (userRole: UserRole | null | undefined): boolean => {
  return canAccessAdminFeatures(userRole);
};

/**
 * Check if user can manage system users
 * SUPERADMIN only
 */
export const canManageUsers = (userRole: UserRole | null | undefined): boolean => {
  return canAccessSuperAdminFeatures(userRole);
};

/**
 * Check if user can view system statistics
 * ADMIN+ only
 */
export const canViewSystemStats = (userRole: UserRole | null | undefined): boolean => {
  return canAccessAdminFeatures(userRole);
};

/**
 * Check if user can modify visibility settings
 * HOST+ only (HOST only for own sessions, ADMIN+ for all)
 */
export const canModifyVisibility = (
  userRole: UserRole | null | undefined,
): boolean => {
  return canAccessHostFeatures(userRole);
};

/**
 * Check if user can access dashboard
 * All authenticated users can access their dashboard
 */
export const canAccessDashboard = (
  userRole: UserRole | null | undefined,
): boolean => {
  return isParticipant(userRole);
};

// ============================================================================
// DASHBOARD ROUTING HELPERS
// ============================================================================

/**
 * Get the appropriate dashboard URL for a user's role
 */
export const getDashboardUrl = (userRole: UserRole | null | undefined): string => {
  if (!userRole) return '/';

  switch (userRole) {
    case 'SUPERADMIN':
      return '/superadmin';
    case 'ADMIN':
      return '/admin';
    case 'HOST':
      return '/host';
    case 'PARTICIPANT':
    default:
      return '/dashboard';
  }
};

/**
 * Get all accessible dashboard URLs for a user
 * Users can access all dashboards at their level and below
 */
export const getAccessibleDashboards = (
  userRole: UserRole | null | undefined,
): string[] => {
  if (!userRole) return [];

  const dashboards: string[] = [];

  if (hasMinimumRole(userRole, 'PARTICIPANT')) {
    dashboards.push('/dashboard');
  }

  if (hasMinimumRole(userRole, 'HOST')) {
    dashboards.push('/host');
  }

  if (hasMinimumRole(userRole, 'ADMIN')) {
    dashboards.push('/admin');
  }

  if (hasMinimumRole(userRole, 'SUPERADMIN')) {
    dashboards.push('/superadmin');
  }

  return dashboards;
};

// ============================================================================
// ROLE UTILITIES
// ============================================================================

/**
 * Get human-readable role name
 */
export const getRoleDisplayName = (role: UserRole): string => {
  const names: Record<UserRole, string> = {
    SUPERADMIN: 'Super Admin',
    ADMIN: 'Administrator',
    HOST: 'Facilitator',
    PARTICIPANT: 'Participant',
  };
  return names[role];
};

/**
 * Get role level number (higher = more permissions)
 */
export const getRoleLevel = (role: UserRole): number => {
  return ROLE_HIERARCHY[role];
};

/**
 * Check if one role is higher than another
 */
export const isRoleHigherThan = (
  userRole: UserRole | null | undefined,
  comparisonRole: UserRole,
): boolean => {
  if (!userRole) return false;
  return ROLE_HIERARCHY[userRole] > ROLE_HIERARCHY[comparisonRole];
};

/**
 * Get all roles in the hierarchy (for admin/testing purposes)
 */
export const getAllRoles = (): UserRole[] => {
  return ['PARTICIPANT', 'HOST', 'ADMIN', 'SUPERADMIN'];
};

/**
 * Get roles that are lower than or equal to the given role
 */
export const getRolesUpTo = (role: UserRole): UserRole[] => {
  const level = ROLE_HIERARCHY[role];
  return Object.entries(ROLE_HIERARCHY)
    .filter(([, roleLevel]) => roleLevel <= level)
    .map(([r]) => r as UserRole);
};

/**
 * Get roles that are higher than the given role
 */
export const getRolesAbove = (role: UserRole): UserRole[] => {
  const level = ROLE_HIERARCHY[role];
  return Object.entries(ROLE_HIERARCHY)
    .filter(([, roleLevel]) => roleLevel > level)
    .map(([r]) => r as UserRole);
};
